<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
  import { FEATURE_REQUEST_CATEGORIES, type FeatureRequestCategory } from '../../lib/types';
  import Modal from '../ui/Modal.svelte';
  import FormField from '../ui/FormField.svelte';
  import Button from '../ui/Button.svelte';

  type Props = {
    onClose: () => void;
    onSuccess: () => void;
  };

  let { onClose, onSuccess }: Props = $props();

  let title = $state('');
  let description = $state('');
  let category = $state<FeatureRequestCategory>('general');
  let submitting = $state(false);
  let error = $state('');

  async function handleSubmit(e: Event) {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      error = 'Please fill in all fields';
      return;
    }

    if (!authState.user) {
      error = 'You must be signed in to submit a feature request';
      return;
    }

    submitting = true;
    error = '';

    try {
      const featureRequestsRef = collection(db, 'featureRequests');
      await addDoc(featureRequestsRef, {
        title: title.trim(),
        description: description.trim(),
        category,
        status: 'open',
        authorId: authState.user.uid,
        authorName: authState.user.displayName || authState.user.email?.split('@')[0] || 'Anonymous',
        authorEmail: authState.user.email,
        voteCount: 1, // Author auto-upvotes
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating feature request:', err);
      error = err.message || 'Failed to create feature request';
    } finally {
      submitting = false;
    }
  }
</script>

<Modal isOpen={true} title="Submit Feature Request" onClose={onClose}>
  <form onsubmit={handleSubmit}>
    {#if error}
      <div class="error-message">{error}</div>
    {/if}

    <FormField label="Title" required>
      <input
        type="text"
        bind:value={title}
        placeholder="Brief summary of your feature idea"
        maxlength="100"
        disabled={submitting}
      />
    </FormField>

    <FormField label="Category" required>
      <select bind:value={category} disabled={submitting}>
        {#each FEATURE_REQUEST_CATEGORIES as cat}
          <option value={cat.id}>{cat.label}</option>
        {/each}
      </select>
    </FormField>

    <FormField label="Description" required>
      <textarea
        bind:value={description}
        placeholder="Describe the feature you'd like to see. What problem does it solve? How would it help you?"
        rows="5"
        disabled={submitting}
      ></textarea>
    </FormField>

    <div class="form-actions">
      <Button variant="secondary" onclick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button type="submit" variant="primary" disabled={submitting}>
        {submitting ? 'Submitting...' : 'Submit Request'}
      </Button>
    </div>
  </form>
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .error-message {
    background: #fee;
    border: 1px solid #fcc;
    color: #c33;
    padding: 0.75rem;
    border-radius: 6px;
    font-size: 0.875rem;
  }

  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.9375rem;
    font-family: inherit;
    transition: border-color 0.2s;
  }

  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: #667eea;
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
</style>
